<template>
    <div class="main-layout__container">
        <div class="content__header content__header--one-item-per-line">
            <div class="content-header__back">
                <UIBackButton
                    :text="'К списку комплектаций'"
                    :to="router.resolve({ name: 'HandbookComplectations' }).href"
                />
            </div>

            <div class="content-header__title">Добавление комплектации</div>

            <div class="content-header__background content-header__background--add-edit-page"></div>
        </div>

        <div class="content__body">
            <div class="form__content">
                <div class="form__fields">
                    <UIBrandImageableRadio
                        v-model="formBrand"
                        :error-message="''"
                    />

                    <div class="form__field-group form__field-group--by-2">
                        <UISelect
                            id="model"
                            v-model="form.model_id"
                            :options="modelOptions"
                            :error-message="errors?.model_id?.join() ?? ''"
                            label="Модель*"
                            searchable
                        />

                        <UIInput
                            id="model_code"
                            v-model="form.code"
                            :error-message="errors?.code?.join() ?? ''"
                            label="Код комплектации*"
                            replace-pattern="[^0-9a-zA-Z]"
                        />
                    </div>
                </div>

                <ComplectationsOptionsForm v-model="form.complectation_option" :errors="complectationOptionsErrors" />
            </div>
        </div>

        <div class="content__footer">
            <div class="form__actions">
                <UISimpleButton
                    button-type="outlined"
                    @clicked="goToComplectationsPage"
                >
                    Выйти без сохранения
                </UISimpleButton>

                <UISimpleButton
                    :loading="saveLoading"
                    @clicked="onSaveComplectation"
                >
                    Сохранить
                </UISimpleButton>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { onMounted, ref, computed, watch } from 'vue';
    import { useRouter } from 'vue-router';

    import { SelectOption } from '@/types/form.type.ts';
    import {
        ComplectationCreateForm,
        ComplectationCreateFormErrors,
        ComplectationUICreateForm,
        makeComplectationEmptyUICreateForm,
        mapComplectationUICreateFormToRequestCreateForm,
    } from '@/types/complectation.type.ts';
    import { Model } from '@/types/model.type.ts';

    import useComplectationRepository from '@/repositories/complectation/useComplectationRepository.ts';
    import useModelRepository from '@/repositories/model/useModelRepository.ts';
    import useNotifications from '@/utils/useNotifications.ts';
    import useUnsavedDataWarning from '@/plugins/useUnsavedDataWarning.ts';

    import UIBackButton from '@/components/ui/UIBackButton/UIBackButton.vue';
    import UIBrandImageableRadio from '@/components/ui/UIBrandImageableRadio/UIBrandImageableRadio.vue';
    import UIInput from '@/components/ui/UIInput/UIInput.vue';
    import UISelect from '@/components/ui/UISelect/UISelect.vue';
    import UISimpleButton from '@/components/ui/UISimpleButton/UISimpleButton.vue';

    import ComplectationsOptionsForm from '@/components/complectations/ComplectationsOptionsForm/ComplectationsOptionsForm.vue';

    const router = useRouter();
    const { createComplectation } = useComplectationRepository();
    const { getAllActiveModels } = useModelRepository();
    const { showSuccess, showError } = useNotifications();
    const { setDirty } = useUnsavedDataWarning();

    const arModels = ref<Model[]>([]);
    const form = ref<ComplectationUICreateForm>(makeComplectationEmptyUICreateForm());
    const formBrandId = ref<number | undefined>(undefined);
    const saveLoading = ref<boolean>(false);
    const errors = ref<ComplectationCreateFormErrors | null>(null);
    const complectationOptionsErrors = ref<string[]>([]);

    const modelOptions = computed<SelectOption[]>(() => {
        if (formBrandId.value === undefined) {
            return [];
        }

        return arModels.value
            .filter((model: Model) => formBrandId.value === undefined || model.brand_id === formBrandId.value)
            .map((model: Model) => ({
                label: model.name,
                value: model.id,
            }));
    });

    const formBrand = computed<string>({
        get() {
            if (formBrandId.value === 1) {
                return 'hyundai';
            }

            if (formBrandId.value === 2) {
                return 'genesis';
            }

            return '';
        },
        set(brandValue: string) {
            if (brandValue === 'hyundai') {
                formBrandId.value = 1;
            }
            else if (brandValue === 'genesis') {
                formBrandId.value = 2;
            }

            if (arModels.value.length > 0) {
                const selectedModel = arModels.value.find((model: Model) => model.id === form.value.model_id);
                if (selectedModel?.brand_id !== formBrandId.value) {
                    form.value.model_id = undefined;
                }
            }
        },
    });

    watch(form, () => {
        setDirty(true);
    }, { deep: true });

    function onSaveComplectation(): void {
        saveLoading.value = true;
        errors.value = null;

        const createForm: ComplectationCreateForm = mapComplectationUICreateFormToRequestCreateForm(form.value);

        if (!validateComplectationOptions(createForm)) {
            saveLoading.value = false;

            showError({
                errors: complectationOptionsErrors.value,
            });

            return;
        }

        createComplectation(createForm)
            .then(() => {
                setDirty(false);

                showSuccess({
                    title: 'Комплектация добавлена',
                });

                goToComplectationsPage();
            })
            .catch((createErrors: ComplectationCreateFormErrors) => {
                errors.value = createErrors;
            })
            .finally(() => {
                saveLoading.value = false;
            });
    }

    function validateComplectationOptions(createForm: ComplectationCreateForm): boolean {
        complectationOptionsErrors.value = [];

        if (Object.keys(createForm.complectation_option).length === 0) {
            complectationOptionsErrors.value.push('Необходимо указать хотя бы одну опцию');
        }

        return complectationOptionsErrors.value.length === 0;
    }

    function goToComplectationsPage(): void {
        router.push({ name: 'HandbookComplectations' });
    }

    function fetchFormOptions(): void {
        getAllActiveModels().then((models: Model[]) => {
            arModels.value = models;
        });
    }

    onMounted(() => {
        fetchFormOptions();
    });
</script>
